import { Settings2 } from 'lucide-react';
import type { CardContext, CardFieldSet } from '../../lib/card-fields.ts';
import { Button } from '../ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.tsx';

/** The chosen fields of one card, label and value, in the catalogue's order. */
export function CardFieldList<T>({
  item,
  set,
  chosen,
  ctx,
}: {
  item: T;
  set: CardFieldSet<T>;
  chosen: readonly string[];
  ctx: CardContext;
}) {
  const rows = set.fields
    .filter((f) => chosen.includes(f.id))
    .map((f) => ({ f, value: f.render(item, ctx) }))
    .filter((r) => r.value !== null && r.value !== undefined && r.value !== '');
  if (rows.length === 0) return null;
  return (
    <dl
      className="text-muted-foreground flex flex-col gap-0.5 text-xs group-data-[density=compact]/board:text-[11px]"
      data-testid="card-fields"
    >
      {rows.map(({ f, value }) => (
        // Compact boards drop the label to fit; it stays a hover away.
        <div key={f.id} className="flex gap-1.5" data-field={f.id} title={f.label}>
          <dt className="shrink-0 group-data-[density=compact]/board:hidden">{f.label}:</dt>
          <dd className="text-foreground/80 min-w-0 truncate">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** "Card fields": which fields this view's cards show. */
export function CardFieldsMenu({
  fields,
  chosen,
  onChange,
}: {
  fields: ReadonlyArray<{ id: string; label: string }>;
  chosen: readonly string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid="card-fields-menu">
          <Settings2 /> Card fields
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Show on cards</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {fields.map((f) => (
          <DropdownMenuCheckboxItem
            key={f.id}
            checked={chosen.includes(f.id)}
            // Keep the menu open: choosing several fields is the usual case.
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(on) =>
              onChange(
                fields.map((x) => x.id).filter((id) => (id === f.id ? on : chosen.includes(id))),
              )
            }
          >
            {f.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
